import {v7 as uuidv7} from "uuid";
import {VoiceProvider} from "@/enums/agencies";
import {ScriptType} from "@/enums/campaigns";
import type {ScriptDraft, ScriptDraftDirection} from "@/services/wingman";
import type {PhoneScript} from "@/types/campaigns";
import type {UseCase} from "@/types/scripts";
import {getScriptEditorPath} from "@/utils/scriptVersioning/paths";
import {DRAFT_VARIABLE_REGEX} from "./draftVariables";

/**
 * Pure logic for turning an assistant-proposed script draft into the payloads
 * the script-save flow (`saveNewScript`) expects: variable normalization
 * against the org's contact fields, plus PhoneScript / use-case construction.
 */

export type DraftVariableAnalysis = {
   /** Draft variable name → the org contact field it resolves to. */
   known: Map<string, string>;
   /** Draft variable names with no matching org contact field, in draft order. */
   unknown: string[];
};

/**
 * Case/format-insensitive key so the model's `{firstName}` or `{First_Name}`
 * resolves to the org's `first_name` contact field.
 */
const canonicalKey = (name: string): string => name.toLowerCase().replaceAll(/[^a-z0-9]/g, "");

const draftTexts = (draft: ScriptDraft): string[] => [
   draft.greeting,
   draft.goodbye,
   draft.voicemail ?? "",
   ...draft.useCases.flatMap((useCase) => [
      useCase.description,
      ...useCase.actions.map((action) => action.description),
   ]),
];

/** Unique `{variable}` names across every text field of the draft, in order of appearance. */
export const extractDraftVariables = (draft: ScriptDraft): string[] => {
   const seen = new Set<string>();
   const names: string[] = [];

   for (const text of draftTexts(draft)) {
      const regex = new RegExp(DRAFT_VARIABLE_REGEX.source, DRAFT_VARIABLE_REGEX.flags);
      let match;
      while ((match = regex.exec(text)) !== null) {
         if (!seen.has(match[1])) {
            seen.add(match[1]);
            names.push(match[1]);
         }
      }
   }

   return names;
};

/**
 * Resolve every draft variable against the org's contact variable fields.
 * The model invents variable names freely, so matching is canonical
 * (case- and separator-insensitive); anything unresolved is reported so the
 * UI can warn before saving.
 */
export const analyzeDraftVariables = (
   draft: ScriptDraft,
   orgFields: string[]
): DraftVariableAnalysis => {
   const fieldsByKey = new Map<string, string>();
   for (const field of orgFields) {
      const key = canonicalKey(field);
      if (!fieldsByKey.has(key)) fieldsByKey.set(key, field);
   }

   const known = new Map<string, string>();
   const unknown: string[] = [];

   for (const name of extractDraftVariables(draft)) {
      const field = fieldsByKey.get(canonicalKey(name));
      if (field !== undefined) {
         known.set(name, field);
      } else {
         unknown.push(name);
      }
   }

   return {known, unknown};
};

/**
 * Rewrite `{variable}` tokens to their resolved org field names — the backend
 * wire format for script content. Unresolved variables pass through unchanged
 * (the user was warned; they save as plain text).
 */
export const normalizeDraftText = (text: string, known: Map<string, string>): string => {
   const regex = new RegExp(DRAFT_VARIABLE_REGEX.source, DRAFT_VARIABLE_REGEX.flags);
   return text.replace(regex, (full, name: string) => {
      const field = known.get(name);
      return field === undefined ? full : `{${field}}`;
   });
};

/**
 * Temp-id prefix recognized by `isNewUseCase` in the script-save flow, so the
 * draft's use cases are POSTed as new and their real ids swapped into the
 * script's associations.
 */
export const DRAFT_USE_CASE_ID_PREFIX = "usecase_draft_";

// The contract type, re-exported so save-flow consumers get it alongside the
// other save helpers.
export type {ScriptDraftDirection};

/** The voice the script will use on calls, as the save payload needs it. */
export type ScriptDraftVoice = {
   id: string;
   provider: VoiceProvider;
};

/**
 * Direction suggested by the draft itself. Current Wingman states it
 * explicitly on the draft (`draft.direction`); drafts persisted before that
 * field existed fall back to the context type, which only names a direction
 * when revising an existing script. The selector in the workspace stays the
 * user's override either way.
 */
export const getDraftDirection = (draft: ScriptDraft): ScriptDraftDirection => {
   if (draft.direction === "inbound" || draft.direction === "outbound") {
      return draft.direction;
   }
   return draft.context.type === "outbound" ? "outbound" : "inbound";
};

export type ScriptSavePayload = {
   script: PhoneScript;
   useCases: Map<string, UseCase>;
};

/**
 * Build the `saveNewScript` inputs from a draft: a new PhoneScript (mirroring
 * the new-script defaults of the script editor) plus the draft's use cases as
 * to-be-created entries keyed by temp id. `agentName`, `direction` and
 * `voice` are user-confirmed in the workspace — the saved script must pass
 * script-editor validation and be usable on calls as-is.
 */
export const buildScriptSavePayload = (
   draft: ScriptDraft,
   known: Map<string, string>,
   agentName: string,
   direction: ScriptDraftDirection,
   voice: ScriptDraftVoice,
   isV2 = false
): ScriptSavePayload => {
   const useCases = new Map<string, UseCase>();

   draft.useCases.forEach((useCase, index) => {
      const id = isV2 ? uuidv7() : `${DRAFT_USE_CASE_ID_PREFIX}${index}`;
      useCases.set(id, {
         id,
         status: "active",
         title: useCase.title,
         description: normalizeDraftText(useCase.description, known),
         actions: useCase.actions.map((action) => ({
            description: normalizeDraftText(action.description, known),
         })),
      });
   });

   const script: PhoneScript = {
      id: "new",
      name: draft.scriptName,
      status: "active",
      direction,
      type: ScriptType.Phone,
      voice: voice.id,
      agentName: agentName.trim(),
      greeting: normalizeDraftText(draft.greeting, known),
      goodbye: normalizeDraftText(draft.goodbye, known),
      voicemail: draft.voicemail ? normalizeDraftText(draft.voicemail, known) : null,
      voiceProvider: voice.provider,
      giveSurvey: false,
      // The backend requires association order >= 1: it rejects 0, even
      // though its validation message reads "Order must be non negative".
      useCaseAssociations: isV2
         ? []
         : Array.from(useCases.keys()).map((id, index) => ({
              id,
              order: index + 1,
              status: "active" as const,
           })),
      ...(isV2 ? {useCases: Array.from(useCases.values())} : {}),
      postCallScripts: [],
      faqs: [],
   };

   return {script, useCases};
};

/** Detail page for a saved script, by the direction it was saved with. */
export const getScriptDetailHref = (
   direction: ScriptDraftDirection,
   scriptId: string,
   versioning = false
): string =>
   getScriptEditorPath({
      direction,
      scriptId,
      revision: "latest",
      versioning,
   });
